import { Temporal } from "@js-temporal/polyfill";
import {
  getTagMeasurementsLast24Hours,
  getTagMeasurementsLast7Days,
  getTagMeasurementsLast30Days,
  getTagMeasurements,
} from "@/api/measurement-api";
import {
  ENALYSIS_TRENDS_DATA_RANGE_MONTHS,
  ENALYSIS_TRENDS_DATA_RANGE_MONTHS_MAX,
} from "@/constants/enalysis-trends-constants.js";

/**
 * Create a custom date range fetch function
 *
 * TIMEZONE HANDLING:
 * - Input dates (YYYY-MM-DD) come from HTML5 date inputs in user's local timezone
 * - Dates are interpreted as local time first (midnight to end of day in user's timezone)
 * - toISOString() then converts to UTC for the API request
 * - This ensures the data returned matches the user's local date selection
 *
 * @param {string} startDate - Start date string (YYYY-MM-DD) from date input (local timezone)
 * @param {string} endDate - End date string (YYYY-MM-DD) from date input (local timezone)
 * @returns {Function} Fetch function for custom date range (converts local to UTC for API)
 */
export function createCustomDateRangeFetch(startDate, endDate) {
  return (assetTagId, assetId) => {
    const requestObject = {
      AssetTagId: assetTagId,
      AssetIdentifier: assetId,
      // Interpret dates as local time (no "Z" suffix), then toISOString() converts to UTC
      // This ensures user's local date selection matches the data returned
      StartDate: new Date(startDate + "T00:00:00").toISOString(),
      EndDate: new Date(endDate + "T23:59:59.999").toISOString(),
    };
    return getTagMeasurements(requestObject);
  };
}

/**
 * Get time range configuration (API function and period format)
 * @param {string} range - Time range string ("24 Hours", "7 Days", "30 Days", "Custom")
 * @param {string} customStartDate - Custom start date (for Custom range)
 * @param {string} customEndDate - Custom end date (for Custom range)
 * @returns {Object} { fn: Function, period: string }
 */
export function getTimeRangeConfig(range, customStartDate, customEndDate) {
  switch (range) {
    case "24 Hours":
      return { fn: getTagMeasurementsLast24Hours, period: "24h" };
    case "7 Days":
      return { fn: getTagMeasurementsLast7Days, period: "7d" };
    case "30 Days":
      return { fn: getTagMeasurementsLast30Days, period: "30d" };
    case "Custom":
      if (customStartDate && customEndDate) {
        // Calculate period based on date difference for chart formatting
        // Use local time interpretation consistent with the API request
        const start = new Date(customStartDate + "T00:00:00");
        const end = new Date(customEndDate + "T23:59:59.999");
        const daysDiff = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
        const period = daysDiff <= 1 ? "24h" : daysDiff <= 7 ? "7d" : "30d";
        return {
          fn: createCustomDateRangeFetch(customStartDate, customEndDate),
          period,
        };
      }
      // Fallback if custom dates not set
      return { fn: getTagMeasurementsLast7Days, period: "7d" };
    default:
      return { fn: getTagMeasurementsLast7Days, period: "7d" };
  }
}

/**
 * Integer months for Enalysis GET /Trends/... lookback so the window is likely to
 * include `startDateStr` (local calendar date) relative to today. Clamped to
 * [ENALYSIS_TRENDS_DATA_RANGE_MONTHS, ENALYSIS_TRENDS_DATA_RANGE_MONTHS_MAX].
 *
 * @param {string} startDateStr - YYYY-MM-DD
 * @param {string} [_endDateStr] - YYYY-MM-DD (reserved; anchor is today)
 * @returns {number}
 */
export function computeEnalysisTrendRangeMonths(startDateStr, _endDateStr) {
  if (!startDateStr) return ENALYSIS_TRENDS_DATA_RANGE_MONTHS;

  let start;
  try {
    start = Temporal.PlainDate.from(startDateStr);
  } catch {
    return ENALYSIS_TRENDS_DATA_RANGE_MONTHS;
  }

  const today = Temporal.Now.plainDateISO();
  const duration = start.until(today, {
    largestUnit: "months",
    smallestUnit: "days",
  });
  const monthSpan = duration.years * 12 + duration.months;
  // Match prior Date-based behavior: inclusive month count forward; no +1 when end < start.
  const months = duration.sign < 0 ? monthSpan : monthSpan + 1;

  return Math.min(
    ENALYSIS_TRENDS_DATA_RANGE_MONTHS_MAX,
    Math.max(ENALYSIS_TRENDS_DATA_RANGE_MONTHS, months)
  );
}
